import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Button from '@mui/material/Button';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { UseDateFieldProps } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  DateView,
  FieldSection,
} from '@mui/x-date-pickers/models';
import updateLocale from 'dayjs/plugin/updateLocale'
import { useTheme } from '@mui/material/styles';

dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
    weekStart: 1,
})

interface DatePickerProps {
  value: Dayjs;
  aggregation: string;
  onDateChange: (newDate: Dayjs | null) => void;
  label?: string;
  minDate?: Dayjs | null;
}

interface ButtonFieldProps
  extends UseDateFieldProps<Dayjs, false>,
    BaseSingleInputFieldProps<
      Dayjs | null,
      Dayjs,
      FieldSection,
      false,
      DateValidationError
    > {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}


function ButtonField(props: ButtonFieldProps) {
  const {
    setOpen,
    label,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;

  return (
    <Button
      variant="outlined"
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      size="small"
      onClick={() => setOpen?.((prev) => !prev)}
      startIcon={<CalendarTodayRoundedIcon fontSize="small" />}
      sx={{ 
        width: '100%',
        height: '40px', // Match the height of the aggregation dropdown
        fontSize: '14px',
        justifyContent: 'flex-start'
      }}
    >
      {label ? `${label}` : 'Pick a date'}
    </Button>
  );
}

const CustomDatePicker: React.FC<DatePickerProps> = ({ value, aggregation, onDateChange, label, minDate: propMinDate }) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  
  // Check user access level
  const access = localStorage.getItem("access");
  const hasPremiumAccess = access && access !== 'null' && access !== 'free';
  
  // Calculate date restrictions - use propMinDate if provided, otherwise fall back to access-based logic
  const getDateRestrictions = () => {
    const today = dayjs();
    
    // If minDate prop is provided, use it; otherwise use access-based logic
    let minDate = propMinDate;
    if (!minDate) {
      const threeMonthsAgo = today.subtract(3, 'month');
      minDate = hasPremiumAccess ? undefined : threeMonthsAgo;
    }
    
    return {
      minDate: minDate,
      maxDate: today // Always prevent future dates
    };
  };
  
  const { minDate, maxDate } = getDateRestrictions();

  function getViews(): DateView[] {
    switch(aggregation) {
      case ('year'):
        return ['year'];
      case ('month'):
        return ['year', 'month'];
      default:
        return ['day']
    }
  }
  
  function getLabel(): string {
    switch(aggregation) {
      case ('year'):
        return 'YYYY';
      case ('month'):
        return 'MMM, YYYY';
      default:
        return 'MMM DD, YYYY'
    }
  }


  let views = ['year']
  if (aggregation === 'month') {
    views.push('month');
  } else if (aggregation !== 'year')
    views.push('month');


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {label && (
          <div style={{ 
            fontSize: '14px', 
            color: theme.palette.text.secondary, 
            marginBottom: '4px',
            fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif'
          }}>
            {label}
          </div>
        )}
        <DatePicker
        value={value}
        label={value == null ? null : value.format(getLabel())}
        views={getViews()}
        minDate={minDate}
        maxDate={maxDate}
        slots={{ field: ButtonField }}
        slotProps={{
          field: { setOpen } as any,
          nextIconButton: { size: 'small' },
          previousIconButton: { size: 'small' },
        }}
        open={open}
        onClose={() => { setOpen(false); }}
        onOpen={() => setOpen(true)}
        onAccept={(newValue) => { setOpen(false); onDateChange(newValue); }}
       />
       </div>
     </LocalizationProvider>
   );
}

export default CustomDatePicker;